/**
 * Regions Map Preview
 * A small interactive map showing geographic region bounding boxes
 */

import React, { useMemo, useRef, useCallback, useEffect } from 'react';
import Map, { Source, Layer, MapRef } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { 
  Box, 
  useTheme, 
  alpha, 
} from '@mui/material';
import {
  Language as GlobeIcon,
} from '@mui/icons-material';
import type { FillLayer, LineLayer, MapLayerMouseEvent } from 'react-map-gl';
import { RegionInfo } from '../../services/api';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';

// Color palette for regions - distinctive colors
export const REGION_COLORS: Record<string, string> = {
  africa: '#10b981',        // emerald
  asia_pacific: '#f59e0b',  // amber
  australia: '#06b6d4',     // cyan
  china: '#ef4444',         // red
  europe: '#3b82f6',        // blue
  middle_east: '#f97316',   // orange
  north_america: '#8b5cf6', // violet
  russia: '#ec4899',        // pink
  south_america: '#84cc16', // lime
  turkey: '#eab308',        // yellow
  uk: '#6366f1',            // indigo
  usa: '#a855f7',           // purple
  worldwide: '#059669',     // green
};

export const getRegionColor = (regionName: string, index: number): string => {
  const normalizedName = regionName.toLowerCase().replace(/[^a-z]/g, '_');
  return REGION_COLORS[normalizedName] || [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', 
    '#06b6d4', '#f97316', '#ec4899', '#84cc16', '#6366f1'
  ][index % 10];
};

interface RegionsMapPreviewProps {
  regions: RegionInfo[];
  height?: number;
  selectedRegion?: string;
  onRegionClick?: (regionName: string) => void;
}

const RegionsMapPreview: React.FC<RegionsMapPreviewProps> = ({ 
  regions, 
  height = 220,
  selectedRegion = '',
  onRegionClick,
}) => {
  const theme = useTheme();
  const mapRef = useRef<MapRef | null>(null);

  // Filter regions with valid bounding boxes (exclude worldwide for mapping)
  const mappableRegions = useMemo(() => {
    return regions.filter(r => 
      r.bbox && 
      r.bbox.length === 4 && 
      r.name !== 'worldwide' &&
      !r.description?.toLowerCase().includes('no geographic')
    );
  }, [regions]);

  // All regions (including worldwide)
  const allRegions = useMemo(() => {
    return regions.filter(r => r.bbox && r.bbox.length === 4);
  }, [regions]);

  // Convert bbox [min_lat, min_lon, max_lat, max_lon] to GeoJSON polygon
  const bboxToPolygon = useCallback((bbox: number[]) => {
    const [minLat, minLon, maxLat, maxLon] = bbox;
    return {
      type: 'Polygon' as const,
      coordinates: [[
        [minLon, minLat], // bottom-left
        [maxLon, minLat], // bottom-right
        [maxLon, maxLat], // top-right
        [minLon, maxLat], // top-left
        [minLon, minLat], // close the polygon
      ]],
    };
  }, []);

  // Create GeoJSON FeatureCollection for all regions
  const geojsonData = useMemo(() => {
    const features = mappableRegions.map((region, index) => ({
      type: 'Feature' as const,
      properties: {
        name: region.name,
        description: region.description || region.name,
        color: getRegionColor(region.name, index),
        index,
      },
      geometry: bboxToPolygon(region.bbox!),
    }));

    return {
      type: 'FeatureCollection' as const,
      features,
    };
  }, [mappableRegions, bboxToPolygon]);

  // Pan to region when selectedRegion changes
  useEffect(() => {
    if (!mapRef.current) return;

    if (!selectedRegion) {
      // Reset to world view
      mapRef.current.flyTo({
        center: [0, 25],
        zoom: 0.8,
        duration: 1000,
      });
      return;
    }

    const region = allRegions.find(r => r.name === selectedRegion);
    if (!region?.bbox) return;

    const [minLat, minLon, maxLat, maxLon] = region.bbox;
    
    // Fit bounds with padding
    mapRef.current.fitBounds(
      [[minLon, minLat], [maxLon, maxLat]],
      {
        padding: { top: 30, bottom: 30, left: 30, right: 30 },
        duration: 1000,
        maxZoom: 6,
      }
    );
  }, [selectedRegion, allRegions]);

  // Reset to world view
  const handleResetView = useCallback(() => {
    if (onRegionClick) {
      onRegionClick('');
    }
  }, [onRegionClick]);

  // Layer styles - highlight selected region
  const fillLayer: FillLayer = {
    id: 'regions-fill',
    type: 'fill',
    source: 'regions',
    paint: {
      'fill-color': ['get', 'color'],
      'fill-opacity': [
        'case',
        ['==', ['get', 'name'], selectedRegion || ''],
        0.4,
        0.15
      ],
    },
  };

  const lineLayer: LineLayer = {
    id: 'regions-line',
    type: 'line',
    source: 'regions',
    paint: {
      'line-color': ['get', 'color'],
      'line-width': [
        'case',
        ['==', ['get', 'name'], selectedRegion || ''],
        3,
        1.5
      ],
      'line-opacity': 0.9,
    },
  };

  // Click on map region to select it
  const onMapClick = useCallback((event: MapLayerMouseEvent) => {
    if (event.features && event.features.length > 0 && onRegionClick) {
      const feature = event.features[0];
      const regionName = feature.properties?.name;
      if (regionName) {
        onRegionClick(regionName);
      }
    }
  }, [onRegionClick]);

  const mapStyle = theme.palette.mode === 'dark' 
    ? 'mapbox://styles/mapbox/dark-v11'
    : 'mapbox://styles/mapbox/light-v11';

  if (mappableRegions.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'relative',
        height,
        borderRadius: '8px',
        overflow: 'hidden',
        border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
        '& .mapboxgl-canvas': {
          borderRadius: '8px',
        },
      }}
    >
      <Map
        ref={mapRef}
        mapboxAccessToken={MAPBOX_TOKEN}
        initialViewState={{
          longitude: 0,
          latitude: 25,
          zoom: 0.8,
        }}
        style={{ width: '100%', height: '100%' }}
        mapStyle={mapStyle}
        attributionControl={false}
        dragPan={true}
        scrollZoom={true}
        doubleClickZoom={true}
        interactiveLayerIds={['regions-fill']}
        onClick={onMapClick}
        cursor="pointer"
      >
        <Source id="regions" type="geojson" data={geojsonData}>
          <Layer {...fillLayer} />
          <Layer {...lineLayer} />
        </Source>
      </Map>

      {/* Reset button when zoomed */}
      {selectedRegion && (
        <Box
          onClick={handleResetView}
          sx={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            px: 1.5,
            py: 0.5,
            borderRadius: '6px',
            bgcolor: alpha(theme.palette.background.paper, 0.9),
            backdropFilter: 'blur(8px)',
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            boxShadow: theme.shadows[1],
            cursor: 'pointer',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            transition: 'all 0.15s ease',
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              borderColor: theme.palette.primary.main,
            },
          }}
        >
          <GlobeIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
        </Box>
      )}
    </Box>
  );
};

export default RegionsMapPreview;
